import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, ChevronsUpDown } from "lucide-react";

const STATE_NAMES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", DC: "District of Columbia",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois",
  IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan", MN: "Minnesota",
  MS: "Mississippi", MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada",
  NH: "New Hampshire", NJ: "New Jersey", NM: "New Mexico", NY: "New York",
  NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma", OR: "Oregon",
  PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota",
  TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia",
  WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

// Module-level cache so the ~3,140-county dataset is fetched once per session.
let _countiesCache = null;
let _countiesPromise = null;
export async function loadCounties() {
  if (_countiesCache) return _countiesCache;
  if (!_countiesPromise) {
    _countiesPromise = api
      .get("/geo/counties")
      .then((r) => { _countiesCache = r.data || {}; return _countiesCache; })
      .catch(() => ({}));
  }
  return _countiesPromise;
}

function Combo({ value, placeholder, options, onSelect, disabled, testid, emptyText, renderLabel, searchText }) {
  const label = renderLabel || ((o) => o);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const fn = searchText || ((o) => o);
    const base = s ? options.filter((o) => fn(o).toLowerCase().includes(s)) : options;
    return base.slice(0, 500);
  }, [q, options, searchText]);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button" disabled={disabled} data-testid={testid}
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#003E87] disabled:cursor-not-allowed disabled:bg-slate-50 disabled:opacity-70"
      >
        <span className={value ? "text-slate-800 truncate" : "text-slate-400 truncate"}>{value ? label(value) : placeholder}</span>
        <ChevronsUpDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      </button>
      {open && !disabled && (
        <div className="absolute z-[60] mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg" data-testid={`${testid}-panel`}>
          <div className="p-2 border-b border-slate-100">
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
              className="h-8 text-sm" data-testid={`${testid}-search`} />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-slate-400">{emptyText || "No matches"}</div>
            )}
            {filtered.map((o) => (
              <button
                key={o} type="button" data-testid={`${testid}-opt`}
                onClick={() => { onSelect(o); setOpen(false); setQ(""); }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50"
              >
                <Check className={`w-3.5 h-3.5 shrink-0 ${value === o ? "opacity-100 text-[#003E87]" : "opacity-0"}`} />
                <span className="truncate">{label(o)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Searchable State -> County selector driven by the bundled US dataset.
export default function StateCountyPicker({ idPrefix = "scp", state, county, onChange, disabled, required }) {
  const [counties, setCounties] = useState({});
  useEffect(() => { loadCounties().then(setCounties); }, []);
  const states = useMemo(() => Object.keys(counties).sort(), [counties]);
  const st = (state || "").toUpperCase();
  const countyOptions = st ? (counties[st] || []) : [];
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className={`text-xs ${required && !st ? "text-amber-600" : ""}`}>State{required ? " *" : ""}</Label>
        <Combo
          testid={`${idPrefix}-state`} value={st} placeholder="Pick a state"
          options={states} disabled={disabled}
          renderLabel={(o) => `${o} — ${STATE_NAMES[o] || o}`}
          searchText={(o) => `${o} ${STATE_NAMES[o] || ""}`}
          onSelect={(s) => onChange({ state: s, county: "" })}
        />
      </div>
      <div>
        <Label className="text-xs">County</Label>
        <Combo
          testid={`${idPrefix}-county`} value={county || ""}
          placeholder={st ? "Pick a county" : "Pick a state first"}
          options={countyOptions} disabled={disabled || !st}
          emptyText="No county matches"
          onSelect={(c) => onChange({ state: st, county: c })}
        />
      </div>
    </div>
  );
}
